import React, { useState, useEffect, useCallback } from "react";
import {
  Grid,
  Card,
  Button,
  TextField,
  Backdrop,
  CircularProgress,
  Alert,
  Snackbar,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import apiRequest from "../../apiRequest";
import { endPoints } from "../../apis";

// Import the new components
import CategoryFormModal from "./components/CategoryFormModal";
import CategoryTable from "./components/CategoryTable";
import DeleteDialog from "./components/DeleteDialog";

function CategoryManagement() {
  const [categories, setCategories] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [accessToken, setAccessToken] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [currentId, setCurrentId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const [categoryData, setCategoryData] = useState({
    name_en: "",
    name_fr: "",
    description_en: "",
    description_fr: "",
    image: null,
    color: "",
    status: "active",
  });

  const initialState = {
    name_en: '',
    name_fr: '',
    description_en: '',
    description_fr: '',
    image: null,
    color: '',
    status: 'active',
  };

  // Fetch categories with pagination
  const getCategories = useCallback(
    async (page, searchValue) => {
      setLoading(true);
      try {
        const params = { page, total: totalItems, search: searchValue };
        const headers = {
          "x-access-token": accessToken,
        };
        const response = await apiRequest.get(endPoints.GET_CATEGORIES,
          params,
          headers,
        );

        if (response.success) {
          const { items, pagination } = response.data;
          setCategories(items);
          setTotalPages(pagination.total_pages);
          setCurrentPage(pagination.current_page);
          setTotalItems(pagination.totalItems);
        }
      } catch (error) {
        console.error("Error fetching category:", error);
        setError("Failed to fetch categories. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [accessToken, totalItems]
  );

  // Fetch categories when currentPage or searchValue changes
  useEffect(() => {
    setAccessToken(localStorage.getItem("access_token"));
    getCategories(currentPage, searchValue);
  }, [currentPage, getCategories, searchValue]);

  // Handle modal open/close
  const handleOpenModal = () => {
    setOpenModal(true);
    setCategoryData(initialState);
  };
  const handleCloseModal = () => {
    setOpenModal(false);
    setCategoryData(initialState);
    setIsEditing(false);
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCategoryData({ ...categoryData, [name]: value });
  };

  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      setCategoryData((prevState) => ({
        ...prevState,
        [fieldName]: file,
        [`${fieldName}_preview`]: URL.createObjectURL(file),
      }));
    }
  };

  // Handle form submission (Add/Edit)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const formData = new FormData();

      formData.append('name_en', categoryData.name_en);
      formData.append('name_fr', categoryData.name_fr);
      formData.append('description_en', categoryData.description_en);
      formData.append('description_fr', categoryData.description_fr);
      formData.append('color', categoryData.color);

      if (categoryData.image instanceof File) {
        formData.append('image', categoryData.image);
      }

      const endPoint = isEditing
        ? `${endPoints.UPDATE_CATEGORY}/${currentId}`
        : `${endPoints.ADD_CATEGORY}`;
      const requestMethod = isEditing ? apiRequest.put : apiRequest.post;

      const response = await requestMethod(endPoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'x-access-token': accessToken,
        },
      });

      if (response.success) {
        setSuccess(isEditing ? "Category updated successfully." : "Category added successfully.");
        getCategories(currentPage, searchValue);
        handleCloseModal();
      } else {
        setError(response.message || 'Failed to add/update Category.');
      }
    } catch (error) {
      console.error('Error:', error);
      setError('An error occurred while processing your request.');
    } finally {
      setLoading(false);
    }
  };

  // Handle edit action
  const handleEdit = (category) => {
    handleOpenModal();
    setIsEditing(true);
    setCategoryData({
      name_en: category.name_en || "",
      name_fr: category.name_fr || "",
      description_en: category.description_en || "",
      description_fr: category.description_fr || "",
      color: category.color || "",
      image: category.image || null,
      image_preview: category.image ? `${apiRequest.BASE_URL}/${category.image}` : null,
      status: category.status || "active",
    });
    setCurrentId(category.id);
  };

  // Handle delete confirmation dialog open
  const handleDeleteClick = (id) => {
    setCurrentId(id);
    setDeleteDialogOpen(true);
  };

  // Handle delete action
  const handleDeleteConfirm = async () => {
    setDeleteDialogOpen(false);
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await apiRequest.delete(
        `${endPoints.DELETE_CATEGORY}/${currentId}`,
        {
          headers: {
            'x-access-token': accessToken,
          },
        }
      );

      if (response.success) {
        setSuccess("Category deleted successfully.");
        setCategories((prev) => prev.filter((categoryData) => categoryData.id !== currentId));
      } else {
        setError(response.message || "Failed to delete the Category.");
      }
    } catch (error) {
      console.error("Error deleting Category:", error);
      setError("An error occurred while deleting the Category.");
    } finally {
      setLoading(false);
    }
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Handle status toggle
  const handleStatusToggle = async (id, currentStatus) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const newStatus = currentStatus === "active" ? "inactive" : "active";
      const payload = { status: newStatus };
      const headers = {
        'Content-Type': 'application/json',
        'x-access-token': accessToken,
      };

      const response = await apiRequest.put(
        `${endPoints.STATUS_CATEGORY}/${id}`,
        payload,
        { headers }
      );

      if (response.success) {
        setSuccess("Category status updated successfully.");
        setCategories((prev) =>
          prev.map((categoryData) =>
            categoryData.id === id ? { ...categoryData, status: newStatus } : categoryData
          )
        );
      } else {
        setError(response.message || 'Failed to update category status.');
      }
    } catch (error) {
      console.error('Error updating category status:', error);
      setError('An error occurred while updating the category status.');
    } finally {
      setLoading(false);
    }
  };

  // Handle drag and drop
  const handleDragEnd = async (result) => {
    setIsDragging(false);

    if (!result.destination) return;

    const items = Array.from(categories);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setCategories(items);

    try {
      setLoading(true);
      const headers = {
        'x-access-token': accessToken, // Add accessToken to headers
      };

      const sequenceData = items.map((item, index) => ({
        id: item.id,
        sequence: index + 1
      }));

      const response = await apiRequest.put(
        endPoints.SEQUENCE_CATEGORY,
        sequenceData,
        { headers }
      );

      if (response.success) {
        setSuccess("Category sequence updated successfully.");
      } else {
        setError(response.message || "Failed to update sequence.");
        getCategories(currentPage, searchValue); // Revert if API call fails
      }
    } catch (error) {
      console.error("Error updating sequence:", error);
      setError("An error occurred while updating the sequence.");
      getCategories(currentPage, searchValue); // Revert if API call fails
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = () => {
    setIsDragging(true);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Categories
                </MDTypography>
              </MDBox>
              <MDBox pt={1} px={1}>
                <MDBox
                  pt={1}
                  px={1}
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="center"
                  gap={2}
                >
                  <TextField
                    label="Search"
                    variant="outlined"
                    margin="normal"
                    placeholder="Search by title or description"
                    style={{ width: "300px" }}
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleOpenModal}
                    style={{
                      whiteSpace: "nowrap",
                      color: "white",
                    }}
                  >
                    Add Category
                  </Button>
                </MDBox>

                <CategoryTable
                  categories={categories}
                  isDragging={isDragging}
                  handleDragEnd={handleDragEnd}
                  handleDragStart={handleDragStart}
                  handleStatusToggle={handleStatusToggle}
                  handleEdit={handleEdit}
                  handleDeleteClick={handleDeleteClick}
                />

                {/* Pagination Controls */}
                <MDBox mt={2} display="flex" justifyContent="center" alignItems="center">
                  <Button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <MDTypography mx={2}>
                    Page {currentPage} of {totalPages}
                  </MDTypography>
                  <Button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      <CategoryFormModal
        open={openModal}
        handleClose={handleCloseModal}
        categoryData={categoryData}
        handleInputChange={handleInputChange}
        handleFileChange={handleFileChange}
        handleSubmit={handleSubmit}
        isEditing={isEditing}
      />

      <DeleteDialog
        open={deleteDialogOpen}
        handleClose={() => setDeleteDialogOpen(false)}
        handleDeleteConfirm={handleDeleteConfirm}
      />

      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Error/Success Snackbar */}
      <Snackbar
        open={!!error || !!success}
        autoHideDuration={3000}
        onClose={() => {
          setError(null);
          setSuccess(null);
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => {
            setError(null);
            setSuccess(null);
          }}
          severity={error ? "error" : "success"}
          sx={{ width: '100%' }}
        >
          {error || success}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default CategoryManagement;